// ===== AX Hub Entrypoint =====
// @inertiajs/svelte v3 + Svelte 5 + Rails 8
// Persistent layout: page.default.layout = AppLayout (assigned in resolve)
// inertia_rails: use_script_element_for_initial_page = true (config/initializers/inertia.rb)

import '../styles/application.css'
import { createInertiaApp, router } from '@inertiajs/svelte'
import { mount } from 'svelte'
import { theme } from '../stores/theme'
import { navigation, isNavigationPending } from '../stores/navigation'
// 레이아웃 없는 공개 페이지 목록
const PUBLIC_PAGES = new Set([
  'Auth/Login', 'Auth/Register', 'Auth/Verify', 'Auth/Verification',
  'Auth/ForgotPassword', 'Auth/ResetPassword',
  'Home',
  'Public/Profile', 'Public/UserPosts', 'Public/UserFollows',
  'Public/Presentations', 'Public/PresentationPdf', 'Public/UserPresentations',
  'Errors/NotFound', 'Pages/Terms', 'Pages/Privacy',
  'Community/Survey', 'Community/SurveyThanks',
])

// ===== CSRF token injection for Rails =====
router.on('before', (event) => {
  const csrfToken = document.querySelector('meta[name="csrf-token"]')?.getAttribute('content')
  if (csrfToken) {
    event.detail.visit.headers['X-CSRF-Token'] = csrfToken
  }
})

// ===== Navigation progress tracking =====
router.on('start', () => { navigation.start() })
router.on('finish', () => { navigation.finish() })
// v3 renamed events: invalid → httpException, exception → networkError
router.on('httpException', () => { navigation.reset() })
router.on('networkError', () => { navigation.reset() })

isNavigationPending.subscribe((pending) => {
  document.documentElement.dataset.inertiaPending = pending ? 'true' : 'false'
})

theme.init()

// ===== Inertia App =====
// Persistent layout: resolve에서 { default, layout } 반환
// ref: https://inertiajs.com/pages#default-layouts (Svelte)
import AppLayout from '../layouts/AppLayout.svelte'

createInertiaApp({
  resolve: name => {
    const pages = import.meta.glob('../pages/**/*.svelte', { eager: true })
    const page = pages[`../pages/${name}.svelte`]

    if (!page) {
      console.error(`Missing Inertia page component: '${name}.svelte'`)
      return
    }

    // Svelte persistent layout: { default: Component, layout: Layout | undefined }
    return {
      default: page.default,
      layout: PUBLIC_PAGES.has(name) ? undefined : (page.layout || AppLayout),
    }
  },
  setup({ el, App, props }) {
    if (el) {
      mount(App, { target: el, props })
      // 랜딩의 크롤러용 정적 본문([data-seo-fallback])은 마운트가 성공한 뒤에만 계속 숨긴다.
      // window.SeoGeo 는 seo_geo_head 가 첫 화면에만 심는다 — 다른 화면에선 없다.
      window.SeoGeo?.mounted()
    } else {
      console.error('Missing Inertia root element')
    }
  },
})

// ===== KakaoTalk In-App Browser → External Browser =====
;(function detectKakao() {
  const ua = navigator.userAgent
  if (!/KAKAOTALK/i.test(ua)) return

  const currentUrl = window.location.href
  const isAndroid = /Android/i.test(ua)
  const isIOS = /iPhone|iPad|iPod/i.test(ua)

  if (isAndroid) {
    const parsed = new URL(currentUrl)
    window.location.href = `intent://${parsed.host}${parsed.pathname}${parsed.search}#Intent;scheme=https;package=com.android.chrome;end`
    return
  }

  if (isIOS) {
    const banner = document.createElement('div')
    banner.id = 'kakao-exit-banner'
    banner.innerHTML = `
      <div style="background:#FFE812;padding:12px 16px;display:flex;align-items:center;justify-content:space-between;gap:12px;font-family:-apple-system,sans-serif;position:fixed;top:0;left:0;right:0;z-index:99999;">
        <span style="font-size:13px;font-weight:600;color:#3C1E1E;">기본 브라우저로 열면 쾌적합니다</span>
        <button id="kakao-exit-btn" style="background:#3C1E1E;color:#FFE812;border:none;padding:6px 14px;border-radius:6px;font-size:12px;font-weight:700;cursor:pointer;white-space:nowrap;">외부 브라우저로 열기</button>
      </div>
    `
    document.addEventListener('DOMContentLoaded', () => {
      document.body.prepend(banner)
      document.body.style.paddingTop = '48px'
      const btn = document.getElementById('kakao-exit-btn')
      if (btn) {
        btn.addEventListener('click', () => {
          location.href = `kakaotalk://web/openExternal?url=${encodeURIComponent(currentUrl)}`
        })
      }
    })
  }
})()

// ===== Service Worker =====
if ('serviceWorker' in navigator) {
  navigator.serviceWorker.register('/service-worker.js', { scope: '/' })
    .then(reg => console.log('SW registered:', reg.scope))
    .catch(err => console.warn('SW registration failed:', err))
}

// ===== PWA Install Prompt =====
;(function setupPWAPrompt() {
  let deferredPrompt = null
  const PWA_DISMISS_KEY = 'pwa-prompt-dismissed-on'
  const IOS_PWA_DISMISS_KEY = 'ios-pwa-dismissed-on'

  function todayKey() {
    const d = new Date()
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`
  }
  function dismissedToday(k) { return localStorage.getItem(k) === todayKey() }
  function dismissForToday(k) { localStorage.setItem(k, todayKey()) }

  window.addEventListener('beforeinstallprompt', (e) => {
    e.preventDefault()
    deferredPrompt = e
    if (dismissedToday(PWA_DISMISS_KEY)) return
    showInstallBanner()
  })

  function showInstallBanner() {
    if (document.getElementById('pwa-install-banner')) return
    const banner = document.createElement('div')
    banner.id = 'pwa-install-banner'
    banner.innerHTML = `
      <div style="position:fixed;bottom:16px;left:16px;right:16px;z-index:99998;background:#0f172a;color:#e2e8f0;padding:16px 20px;border-radius:16px;display:flex;align-items:center;justify-content:space-between;gap:12px;box-shadow:0 8px 32px rgba(0,0,0,0.24);font-family:'SUIT Variable', sans-serif;">
        <div>
          <div style="font-size:14px;font-weight:700;">AX Hub 앱 설치</div>
          <div style="font-size:12px;color:#94a3b8;margin-top:2px;">홈 화면에 추가하여 빠르게 접속하세요</div>
        </div>
        <div style="display:flex;gap:8px;flex-shrink:0;">
          <button id="pwa-install-btn" style="background:#3b82f6;color:#fff;border:none;padding:8px 16px;border-radius:9999px;font-size:13px;font-weight:600;cursor:pointer;">설치</button>
          <button id="pwa-dismiss-btn" style="background:transparent;color:#94a3b8;border:1px solid #334155;padding:8px 12px;border-radius:9999px;font-size:13px;cursor:pointer;">오늘 하루 더 이상 안 보기</button>
        </div>
      </div>
    `
    document.body.appendChild(banner)
    document.getElementById('pwa-install-btn')?.addEventListener('click', async () => {
      if (!deferredPrompt) return
      deferredPrompt.prompt()
      await deferredPrompt.userChoice
      deferredPrompt = null
      banner.remove()
    })
    document.getElementById('pwa-dismiss-btn')?.addEventListener('click', () => {
      dismissForToday(PWA_DISMISS_KEY)
      banner.remove()
    })
  }

  const isIOS = /iPhone|iPad|iPod/.test(navigator.userAgent)
  const isStandalone = window.navigator.standalone === true
  if (isIOS && !isStandalone && !/KAKAOTALK/i.test(navigator.userAgent)) {
    if (!dismissedToday(IOS_PWA_DISMISS_KEY)) {
      document.addEventListener('DOMContentLoaded', () => {
        setTimeout(() => {
          if (document.getElementById('ios-pwa-tip')) return
          const tip = document.createElement('div')
          tip.id = 'ios-pwa-tip'
          tip.innerHTML = `
            <div style="position:fixed;bottom:16px;left:16px;right:16px;z-index:99998;background:#fff;color:#1e293b;padding:16px 20px;border-radius:16px;box-shadow:0 8px 32px rgba(0,0,0,0.15);font-family:'SUIT Variable', sans-serif;text-align:center;">
              <div style="font-size:14px;font-weight:700;margin-bottom:8px;">AX Hub를 홈 화면에 추가하세요</div>
              <div style="font-size:12px;color:#64748b;line-height:1.5;">하단 <span style="font-size:16px;">⎋</span> 공유 버튼 → <strong>홈 화면에 추가</strong></div>
              <button id="ios-pwa-dismiss-btn" style="margin-top:12px;background:none;border:none;color:#94a3b8;font-size:12px;cursor:pointer;">오늘 하루 더 이상 안 보기</button>
            </div>
          `
          document.body.appendChild(tip)
          document.getElementById('ios-pwa-dismiss-btn')?.addEventListener('click', () => {
            dismissForToday(IOS_PWA_DISMISS_KEY)
            tip.remove()
          })
        }, 3000)
      })
    }
  }
})()
